import { observable } from "mobx";

export enum Role {
  user = "user",
  model = "model",
  tool = "tool",
}

export enum ToolKind {
  unknown = "unknown",
  dryrunQuery = "dry_run_query",
  runQuery = "run_query",
  choices = "choices",
  yesNo = "yes_no",
  okCancel = "ok_cancel",
  listFiles = "list_files",
  readFile = "read_file",
  replaceFile = "replace_file",
  completeReplaceFile = "complete_replace_file",
  cancelReplaceFile = "cancel_replace_file",
  listBigqueryFunctions = "list_bigquery_functions",
  getBigqueryFunction = "get_bigquery_function",
  drawGraph = "draw_graph",
}

export interface FunctionCallEvent {
  callKey: string;
  name: string;
  args: Record<string, unknown>;
}

export interface ToolUse {
  callKey: string;
  toolKind: ToolKind;
  parameters: ToolParameter[];
}

export interface ToolParameter {
  key: string;
  value: string;
}

export const hideUserInstructions = (prompt: string) =>
  prompt.replace(/^(?:<user_instruction>\n.*?\n<\/user_instruction>\n)+/s, "");

export class ChatMessage {
  private text: string = "";
  readonly turnKey: string = "";
  readonly callKey: string = "";
  readonly role: Role = Role.user;
  readonly toolKind: ToolKind = ToolKind.unknown;
  @observable accessor thoughtsText: string = "";
  @observable accessor processedText: string = "";
  @observable accessor toolUses: ToolUse[] = [];
  @observable accessor isCompleted = false;

  constructor(
    text: string,
    role: Role,
    option?: { turnKey?: string; callKey?: string; toolKind?: ToolKind },
  ) {
    this.text = text;
    this.processedText = text;
    this.role = role;
    this.turnKey = option?.turnKey ?? "";
    this.callKey = option?.callKey ?? "";
    if (option?.toolKind) {
      this.toolKind = option.toolKind;
    }
  }

  public add(text: string, thought: boolean) {
    if (thought) {
      this.thoughtsText += text;
    } else {
      this.text += text;
      this.processedText = this.text;
    }
  }

  public addToolCalls(calls: FunctionCallEvent[]) {
    this.toolUses.push(...calls.map((call) => this.toToolUse(call)));
  }

  public reset() {
    this.text = "";
    this.processedText = "";
    this.thoughtsText = "";
    this.toolUses = [];
  }

  public get isUserSelection() {
    return (
      this.role == Role.tool &&
      [ToolKind.choices, ToolKind.yesNo, ToolKind.okCancel].includes(
        this.toolKind,
      )
    );
  }

  public get userSelection(): string | undefined {
    if (!this.isUserSelection) {
      return;
    }
    try {
      const response = JSON.parse(this.processedText);
      if (typeof response.choice == "string") {
        return response.choice;
      }
    } catch {
      return;
    }
  }

  private toToolUse(call: FunctionCallEvent): ToolUse {
    const parameters = Object.entries(call.args).flatMap(([key, value]) => {
      if (call.name == "choices" && key == "choices" && Array.isArray(value)) {
        return value.map((choice) => ({
          key: "choice",
          value: String(choice),
        }));
      }
      return [
        {
          key,
          value: typeof value == "string" ? value : JSON.stringify(value),
        },
      ];
    });
    return {
      callKey: call.callKey,
      toolKind: call.name as ToolKind,
      parameters,
    };
  }
}
